import type { ProviderDetail } from '@katoa/schema-provider'
import { listSentence } from './list-sentence'
import { pluralize } from './plural'
import { formatProviderNameLead } from './provider-name'
import { fitDescription } from './seo'
import { SITE_NAME } from './site'

// The `<meta name="description">` for a provider page (VIT-18206).
//
// The description this replaces named the *categories* of data the page holds —
// "Specialty, credentials, ratings and clinic contact details for Jane Doe in
// Austin, TX (NPI 1234567890)" — with only the name, the city and the NPI varying
// across ~100k pages. Two problems with that, and they compound:
//
//  - It tells a reader nothing they couldn't have guessed from the title, so it
//    earns no click.
//  - It gives Google no reason to keep it, and a description Google finds
//    unhelpful is one it replaces with text scraped off the page — the same
//    failure VIT-18200 fixed for results pages.
//
// So this states facts about the specific provider instead: their rating, the
// specialties they practise beyond their primary one, whether they are taking
// patients, how their costs compare, what they are certified in, where they
// trained. All of it already on `ProviderDetail` and already rendered on the page —
// nothing here is a claim the page doesn't also make.
//
// Google's own guidance is explicit that this is the right shape for a site like
// this one: it encourages "programmatic generation of the descriptions" for
// database-driven sites, asks for descriptions that are "unique ... for each page",
// and says page-specific data is the good candidate for generating them. It also
// notes a description "doesn't just have to be in sentence format" — a clause list
// of concrete facts is a form it names approvingly, as long as it isn't a string of
// keywords.
// https://developers.google.com/search/docs/appearance/snippet
//
// Clause ORDER is load-bearing, not just cosmetic. `DESCRIPTION_MAX_LENGTH` is the
// desktop SERP budget, but two audiences cut earlier: mobile snippets truncate
// around 120 characters, and so do the social cards this same string feeds through
// `og:description` (X at roughly 125, LinkedIn lower). So the identity lead and the
// two highest-value clauses are sized to land inside the first ~120 — anything
// after that is a desktop-only bonus rather than the substance.
//
// Pure functions, no router and no I/O, so the copy is unit-testable without
// rendering the route.

/**
 * The description for a provider page with no provider — the 404 and error
 * boundary renders, where `loaderData` is undefined.
 *
 * Deliberately says nothing about any provider: there isn't one, and a page that
 * failed to load must not describe itself as a record it doesn't hold.
 */
export const GENERIC_PROVIDER_DESCRIPTION = `Compare doctors on cost, quality and the actual health outcomes of other patients like you, on ${SITE_NAME}.`

/** Trimmed value, or null when absent or whitespace-only. Mirrors provider.tsx's `isNonEmpty`. */
const trimmed = (value: string | null | undefined): string | null => {
  const next = value?.trim() ?? ''
  return next === '' ? null : next
}

/**
 * How many focus tags the description names.
 *
 * Two, not the whole array. The tags are raw upstream text of unbounded length
 * ("Micrographic Dermatologic Surgery"), and a third one mostly buys a clause that
 * the 160-character budget then sheds anyway — so the cap keeps the more valuable
 * clauses behind it in play.
 */
const MAX_FOCUS_TAGS = 2

/**
 * The `efficiencyIndex` at or above which the provider's costs are worth naming.
 *
 * Ribbon's index runs 1–5 with higher meaning *cheaper* (see DollarRating, which
 * inverts it to render `$` glyphs), so 4 and 5 are the two below-average bands.
 *
 * Only the favourable side is surfaced, deliberately. "Above-average visit costs"
 * is true of two fifths of providers and is not something a page says about the
 * person it is describing; a SERP snippet is our copy about them, not a
 * comparison table. The page itself still shows the full $-tier either way, so
 * nothing is hidden — the cost simply goes unmentioned where it does not help.
 */
const CHEAPER_THAN_AVERAGE = 4

/**
 * "Jane C. Doe, MD, specializes in Dermatology in Austin, TX." — who this page is
 * about.
 *
 * Always first and never shed: the name and the location are what make the
 * description match the query that found it, and the location is also what tells
 * same-named providers apart (VIT-18030).
 *
 * `nameWithMiddleInitial`, not `name`, for that same reason — and via
 * `formatProviderNameLead` so the post-nominals hug the name the way they do on the
 * card rather than reading "Jane C. DoeMD" (VIT-18200), with the comma pair they
 * open closed off before the sentence continues (VIT-18212).
 *
 * One sentence, not two (VIT-18212). This used to read "Jane C. Doe, MD.
 * Dermatology in Austin, TX." — a name, a full stop, then a fragment with no verb,
 * which is the shape of a database field printed rather than of copy written about
 * a person. The verb costs ~14 characters of a 160-character budget, which is close
 * to a whole clause, and it is worth it: the lead is the part of the snippet every
 * reader sees (mobile truncates around 120 characters), so it is the part that has
 * to read like a sentence.
 *
 * Every separator is still conditional, because any of the three parts can be
 * missing: a provider with no specialty reads "Jane C. Doe, MD, in Austin, TX",
 * and one with no address reads "Jane C. Doe, MD, specializes in Dermatology."
 * rather than trailing "in ".
 *
 * Null only when there is no name at all. The endpoint guarantees one (it falls
 * back to the bare NPI), so this is type-honesty rather than a case we expect.
 */
const identityLead = (provider: ProviderDetail): string | null => {
  const cityState = [provider.address?.city, provider.address?.state]
    .map(trimmed)
    .filter((value): value is string => value != null)
    .join(', ')
  const specialty = trimmed(provider.specialty)

  // The sentence continues past the name whenever there is a specialty or a
  // location to name — which is what decides the closing comma on the credentials.
  const name = formatProviderNameLead(
    provider.nameWithMiddleInitial,
    provider.degreeSuffix,
    specialty != null || cityState !== '',
  )
  if (name === '') return null

  if (specialty != null) {
    return cityState === ''
      ? `${name} specializes in ${specialty}.`
      : `${name} specializes in ${specialty} in ${cityState}.`
  }

  return cityState === '' ? `${name}.` : `${name} in ${cityState}.`
}

/**
 * A rating as it reads beside its denominator: "5", not "5.0", and "4.8" as-is.
 *
 * A trailing ".0" is precision the number does not have — "5.0/5" claims a
 * hundredth of a star that the average never resolved — and it reads as a rendered
 * float rather than as a rating. The page's own star row keeps `toFixed(1)`, and
 * deliberately: there the number sits in a column of other numbers where a ragged
 * decimal place would look broken, while here it sits mid-sentence.
 *
 * Still one decimal place for everything else, so 4.75 does not arrive as
 * "4.75/5" — the extra digit is noise at snippet length, and it is not a precision
 * we would stand behind either.
 */
const formatRating = (rating: number): string => {
  const rounded = rating.toFixed(1)
  return rounded.endsWith('.0') ? rounded.slice(0, -2) : rounded
}

/**
 * The clauses that make one provider's description different from the next one's,
 * in descending order of what a reader scanning a SERP would want to know.
 *
 * The order is the shed order too — `providerDescription` drops from the end — so
 * it is a priority list, not just a sentence plan. Rating leads because it is the
 * one number people already scan search results for; the focus tags follow because
 * a subspecialty is what separates two providers whose primary specialty is the
 * same; the credentials trail because they reassure rather than differentiate
 * (nearly every provider has some).
 *
 * Each clause is punctuation-free — the caller joins them with ". " and closes the
 * sentence — and each is omitted entirely rather than rendered empty, so a thin
 * record produces a shorter description instead of one full of blanks.
 */
const substanceClauses = (provider: ProviderDetail): string[] => {
  const clauses: string[] = []

  // "Rated 4.8/5 from 126 reviews". The count is what makes the average mean
  // anything, so it is included whenever there is one — a 5/5 from a single
  // review is a different claim from a 4.8 from 126, and stating the first as
  // "Rated 5" alone would be the more flattering and less honest of the two.
  //
  // The denominator is explicit in both branches (VIT-18212). "Rated 4.8" alone
  // relies on the reader assuming a five-point scale, which most will and none
  // should have to — a ten-point scale is not an absurd reading of a bare 4.8, and
  // it inverts the meaning. Three characters, and it is the one number in the
  // snippet people actually compare between two results.
  const { ratingsAvg, ratingsCount } = provider
  if (typeof ratingsAvg === 'number') {
    const rated = `Rated ${formatRating(ratingsAvg)}/5`
    clauses.push(
      ratingsCount != null && ratingsCount > 0
        ? `${rated} from ${ratingsCount} ${pluralize(ratingsCount, 'review')}`
        : rated,
    )
  }

  // "Areas of focus", NOT "Treats". These are specialties, not conditions: the
  // endpoint builds `focusTags` from NPPES taxonomy descriptions, Ribbon board
  // certification names and Ribbon provider types (`focusTags` in
  // apps/providers/src/core/provider-detail.ts), so a real record yields
  // "Cardiology", "Internal Medicine" — and "Treats Cardiology" is not English.
  //
  // The specialty is excluded for the same reason, and it is not an edge case:
  // `specialty` is resolved from those same three sources in that same order, so
  // `focusTags[0]` is usually the value already in the identity lead. Without this
  // the second-ranked clause of most descriptions would read "Dermatology in
  // Austin, TX. Areas of focus: Dermatology" — a wasted clause restating the lead.
  const specialty = trimmed(provider.specialty)?.toLowerCase()
  const focusTags = provider.focusTags
    .map(trimmed)
    .filter((tag): tag is string => tag != null && tag.toLowerCase() !== specialty)
    .slice(0, MAX_FOCUS_TAGS)
  if (focusTags.length > 0) {
    clauses.push(`Areas of focus: ${listSentence(focusTags)}`)
  }

  // Ribbon's own comparison, not ours — see CHEAPER_THAN_AVERAGE for why only the
  // favourable bands are named.
  const { efficiencyIndex } = provider
  if (efficiencyIndex != null && Math.round(efficiencyIndex) >= CHEAPER_THAN_AVERAGE) {
    clauses.push('Below-average visit costs')
  }

  // `some`, not `locations[0]`: a provider can be closed at their main practice
  // and open at another, and the page shows the flag per location. Explicitly
  // `=== true` because the field is nullable and unknown is not yes.
  if (provider.locations.some((location) => location.acceptingNewPatients === true)) {
    clauses.push('Accepting new patients')
  }

  // The board that certified them where it is named, the certification itself
  // otherwise — "Board certified in Dermatology" either way, since the two fields
  // carry the same kind of string and only one of them is guaranteed.
  const certification = provider.certifications[0]
  const board = certification == null ? null : (trimmed(certification.board) ?? trimmed(certification.name))
  if (board != null) {
    clauses.push(`Board certified in ${board}`)
  }

  const school = trimmed(provider.educations[0]?.name)
  if (school != null) {
    clauses.push(`Trained at ${school}`)
  }

  return clauses
}

/**
 * The description for a provider page, inside `DESCRIPTION_MAX_LENGTH`.
 *
 * Built as a ladder of candidates for `fitDescription`: the identity lead plus
 * every clause, then one fewer clause, and so on. The budget therefore costs the
 * *least* valuable facts rather than cutting the sentence mid-thought, which is
 * what would happen if this were one long string handed to a truncator.
 *
 * Commas and periods only, no em dashes. An em dash would set the clauses off more
 * cleanly, but em dashes in generated copy read as machine-written — the exact
 * impression a SERP snippet cannot afford (same call as results.tsx).
 *
 * Every rung carries the NPI, which is the one field on the record that cannot
 * collide. It is what makes the description unique per page even where the title
 * is not: 18+ NPIs share a name AND a practice address (two Robert Gilsons, both
 * dermatologists at one San Antonio address), and same-named providers can share a
 * middle initial or carry no middle name at all — so neither the lead nor the
 * clauses can be relied on to differ. That is the duplicate-description defect
 * VIT-18030 exists to close, and Google asks for "unique descriptions for each
 * page" in as many words.
 *
 * It costs 16 characters of the budget on every page, which is roughly one clause,
 * and it is a deliberate trade: the least valuable clause is worth less than the
 * guarantee. An NPI is also a term people search, so it is not dead weight.
 *
 * Two rungs sit below the clauses, for records the clauses cannot describe:
 *
 *  - `floor`, for a record thin enough to produce no clauses at all (no rating, no
 *    tags, no certification, nothing). It still says what the page is for, which a
 *    bare name and city does not.
 *  - `identified`, the lead plus the NPI and nothing else, for a name long enough
 *    that the floor's boilerplate no longer fits.
 *
 * The bare lead is last and is the only rung without the NPI, for a name long
 * enough that even ` NPI 1234567890.` overruns. Truncating the rung above it would
 * cut into the digits and leave an ellipsis mid-identifier, which states a wrong
 * NPI rather than none.
 */
export const providerDescription = (provider: ProviderDetail): string => {
  const lead = identityLead(provider)
  if (lead == null) return GENERIC_PROVIDER_DESCRIPTION

  const clauses = substanceClauses(provider)
  const identifier = `NPI ${provider.npi}.`
  const floor = `${lead} Compare cost, quality and real patient outcomes on ${SITE_NAME}. ${identifier}`
  const identified = `${lead} ${identifier}`

  // Every clause, then all but the last, then all but the last two — descending
  // preference, the contract `fitDescription` takes.
  const named = clauses.map(
    (_, index) => `${lead} ${clauses.slice(0, clauses.length - index).join('. ')}. ${identifier}`,
  )

  const [preferred = floor, ...fallbacks] = [...named, floor, identified, lead]
  return fitDescription(preferred, ...fallbacks)
}
